import {
  ActionTask,
  CodeTask,
  Conditional,
  ContinuousLoading,
  DataConnector,
  JsonSerializableOutput,
  Layout,
  ScriptFunctionInfo,
  WorkcellInstrument,
  WorkflowConfig,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';

import { getDeserializedGraphSlice } from './getDeserializedGraphSlice';
import {
  convertToTaskParameters,
  getConditionalAppliedParameters,
  getDataExportParameters,
  getDataMode,
  getInstrumentForTask,
  getLabwareIdsToInputSlots,
  getLabwareIdsToOutputSlots,
  getLabwareIdsToUnitsConsumed,
  getLabwareTypeFromLabwareItem,
  getScriptName,
  getSlot,
  getTaskAppliedParameters,
} from './helpers';
import { TaskType as TaskTypeUnion } from './types';
import { DEFAULT_LABWARE_CONTENT } from '../../constants';
import { DriversByNameAndVersion } from '../../data/drivers';
import { SDKInteropError, UnsupportedFeatureError } from '../../errors';
import {
  WorkcellInstrumentsById,
  WorkflowState,
} from '../../features/workflow/types';
import { NULL_LABWARE_TYPE } from '../../features/workflowJsonSelectors/constants';
import { LATEST_WORKFLOW_MIGRATION_VERSION } from '../../migration';
import {
  AllTaskProperties,
  LabwareProperties,
  PropertiesType,
} from '../../types';
import { getClosureStateOptionsForLabwareType, getNickname } from '../../utils';
import { DataMode } from '../../zodSchemas/taskProperties';

const handleTask = (
  task: TaskTypeUnion,
  processedTaskAcc: Record<string, AllTaskProperties>,
  rawTaskAcc: Record<string, TaskTypeUnion>,
  workflowConfigWorkcellInstruments: WorkcellInstrument[],
  functions: ScriptFunctionInfo[]
) => {
  switch (task.type) {
    case 'action': {
      task satisfies ActionTask;

      rawTaskAcc[task.id] = task;
      processedTaskAcc[task.id] = {
        id: task.id,
        taskType: task.action,
        // todo: update this to use task.instrument.id when available from the API https://automata-tech.atlassian.net/browse/G2-1593
        instrumentId: getInstrumentForTask(
          task,
          workflowConfigWorkcellInstruments
        ).id,
        nickname: task.description ?? task.action,
        taskParameters: convertToTaskParameters(
          task.arguments,
          task.labware_sources
        ),
        userTimeEstimate:
          typeof task.time_estimate === 'number' ? task.time_estimate : null,
        labwareIdsToUnitsConsumed: getLabwareIdsToUnitsConsumed(task),
        labwareIdsToInputSlots: getLabwareIdsToInputSlots(task),
        labwareIdsToOutputSlots: getLabwareIdsToOutputSlots(task),
        propertiesType: PropertiesType.Action,
        inputCsvDataMode: getDataMode(task.inputs, 'input_csv_file'),
        workflowDataMode: getDataMode(task.inputs, 'workflow_data'),
        plateMapDataMode: getDataMode(task.inputs, 'plate_map_csv'),
        taskAppliedParameters: getTaskAppliedParameters(task.inputs),
        // isLabwareLessTask. this is probably fine to leave as undefined - there is no sdk equivalent
      };
      break;
    }
    case 'conditional': {
      task satisfies Conditional;

      for (const subTask of Object.values(task.tasks)) {
        handleTask(
          subTask,
          processedTaskAcc,
          rawTaskAcc,
          workflowConfigWorkcellInstruments,
          functions
        );
      }

      if (task?.next) {
        handleTask(
          task.next,
          processedTaskAcc,
          rawTaskAcc,
          workflowConfigWorkcellInstruments,
          functions
        );
      }

      // check/notification for unsupported else/if structure could go here
      if (task.comparison === 'Else') {
        break;
      }

      rawTaskAcc[task.id] = task;
      processedTaskAcc[task.id] = {
        id: task.id,
        taskType: 'conditional',
        nickname: task.id,
        comparison: task.comparison,
        propertiesType: PropertiesType.Conditional,
        expectedOutcome: task.is_expected ? 'IF condition' : 'ELSE condition',
        elseLabel: 'Else', // it doesn't look like these are settable in the API
        ifLabel: 'If', // so we can default them for now
        taskAppliedParameters: getConditionalAppliedParameters(task), // todo: https://automata-tech.atlassian.net/browse/G2-1734
      };
      break;
    }
    case 'code': {
      task satisfies CodeTask;

      if (task.function.type === 'lua') {
        throw new UnsupportedFeatureError(
          'LUA code tasks are not currently supported by Canvas.'
        );
      }

      rawTaskAcc[task.id] = task;
      processedTaskAcc[task.id] = {
        id: task.id,
        taskType: task.type,
        nickname: task.description ?? `Code task: ${task.id}`,
        scriptName: getScriptName(task, functions),
        propertiesType: PropertiesType.Code,
        taskAppliedParameters: getTaskAppliedParameters(task.inputs),
        // taskParameters: getCodeTaskParameters(task.inputs), // task params not supported for code tasks
        // labwareIdsToInputSlots: getLabwareIdsToInputSlots(task),
        // labwareIdsToOutputSlots: getLabwareIdsToOutputSlots(task),
        // inputCsvDataMode: getDataMode(task.inputs, 'input_csv_file'),
        // workflowDataMode: getDataMode(task.inputs, 'workflow_data'),
        // plateMapDataMode: getDataMode(task.inputs, 'plate_map_csv'),
      };

      break;
    }
    case 'data_connector': {
      task satisfies DataConnector;

      if (task.action.toLowerCase() === 'import') {
        throw new UnsupportedFeatureError(
          'Data import tasks are not currently supported by Canvas.'
        );
      }

      rawTaskAcc[task.id] = task;
      processedTaskAcc[task.id] = {
        id: task.id,
        taskType: task.type,
        nickname: task.description ?? `Data export: ${task.id}`,
        workflowDataMode: DataMode.Manual,
        propertiesType: PropertiesType.DataExport,
        taskAppliedParameters: getTaskAppliedParameters(task.inputs),
        taskParameters: getDataExportParameters(task.inputs),
        connectorType: task.data_connector_type,
      };

      break;
    }
    case 'continuous_loading':
      task satisfies ContinuousLoading;

      for (const subTask of Object.values(task.tasks)) {
        handleTask(
          subTask,
          processedTaskAcc,
          rawTaskAcc,
          workflowConfigWorkcellInstruments,
          functions
        );
      }

      rawTaskAcc[task.id] = task;
      processedTaskAcc[task.id] = {
        id: task.id,
        taskType: 'continuousLoading',
        nickname: task.description ?? task.id,
        propertiesType: PropertiesType.ContinuousLoading,
        description: task.description ?? '',
        additionalDataInputs: task.additional_data_inputs,
      };
      break;
    default:
      // todo: fix task.type discriminator so that we can get proper type narrowing
      // task satisfies never;

      throw new SDKInteropError(
        `Unsupported task type "${task.type}" for task ID "${task.id}"`
      );
  }
};

export const createLayout = (
  workflowConfig: WorkflowConfig,
  workcellInstruments: WorkcellInstrumentsById,
  functions: ScriptFunctionInfo[],
  driversByNameAndVersion: DriversByNameAndVersion
): Layout => {
  const labwareProperties = workflowConfig.workflow.labware.reduce<
    Record<string, LabwareProperties>
  >((acc, labwareItem) => {
    const labwareType = getLabwareTypeFromLabwareItem(labwareItem.type);
    const labware: LabwareProperties = {
      id: labwareItem.id,
      labwareType,
      content: DEFAULT_LABWARE_CONTENT,
      contentDescription: null,
      startLocation:
        ('instrument' in labwareItem.location &&
          labwareItem.location.instrument) ||
        null,
      labwareDefinitionURI:
        labwareItem.type === NULL_LABWARE_TYPE ? null : labwareItem.type,
      nickname: labwareItem.description ?? getNickname(labwareType),
      maxIdleTimeExists: typeof labwareItem.max_idle_time === 'number',
      maxIdleTime: labwareItem.max_idle_time ?? null,
      slot: getSlot(labwareItem.location),
      totalUnits: labwareItem.total_units,
      singleOrMultiUse: labwareItem.total_units > 1 ? 'multi' : 'single', // https://automata-tech.atlassian.net/browse/G2-1596?focusedCommentId=60001
      // closureState doesn't seem to be used in the API
      // https://automata-tech.atlassian.net/browse/G2-1596?focusedCommentId=60000
      closureState:
        getClosureStateOptionsForLabwareType(labwareType)[0] ?? 'open',
    };

    return {
      ...acc,
      [labwareItem.id]: labware,
    };
  }, {});

  const taskPropertiesAcc: Record<string, AllTaskProperties> = {};
  const flatTasksById: Record<string, TaskTypeUnion> = {};

  Object.values(workflowConfig.workflow.tasks).forEach((task) => {
    handleTask(
      task,
      taskPropertiesAcc,
      flatTasksById,
      workflowConfig.workcell.instruments,
      functions
    );
  });

  const visualWorkflow = {
    labwareProperties,
    taskProperties: taskPropertiesAcc,
    workflowAuthor: workflowConfig.metadata.author,
    workflowDescription: workflowConfig.metadata.description,
    workflowName: workflowConfig.name,
    comments: {},
    workcellName: workflowConfig.workcell.workcell_id || '', // this is workcell ID - is it the same?
    batches:
      typeof workflowConfig.workflow.batches === 'number'
        ? workflowConfig.workflow.batches
        : 1, // gotcha with params here?
    parameterDefinitions: workflowConfig.parameter_definitions,
    // parameterValues: todo: are these needed for reading in an existing workflow?
  } satisfies WorkflowState['visualWorkflow'];

  // if we don't cast here, labwareNodes/taskNodes are incorrectly flagged as non-JSON serializable
  const graph = getDeserializedGraphSlice(
    workflowConfig.workflow,
    flatTasksById,
    functions,
    workflowConfig.workcell.instruments,
    driversByNameAndVersion
  ) as unknown as JsonSerializableOutput;

  const layout: Layout = {
    data: {
      graph,
      visualWorkflow,
      workcellInstruments,
      selectedWorkcellId: workflowConfig.workcell.workcell_id,
    },
    version: LATEST_WORKFLOW_MIGRATION_VERSION.toString(),
  };

  return layout;
};

export const addMissingLayout = (
  workflow: WorkflowConfig,
  workcellInstruments: WorkcellInstrumentsById,
  functions: ScriptFunctionInfo[],
  driversByNameAndVersion: DriversByNameAndVersion
): WorkflowConfig => {
  return {
    ...workflow,
    workflow: {
      ...workflow.workflow,
      layout: createLayout(
        workflow,
        workcellInstruments,
        functions,
        driversByNameAndVersion
      ),
    },
  };
};
